import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationAPI } from './notificationAPI';

// Async thunks
export const fetchNotificationSettings = createAsyncThunk(
  'notification/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getNotificationSettings();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLevels = createAsyncThunk(
  'notification/fetchLevels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getLevels();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrCreateSettings = createAsyncThunk(
  'notification/fetchOrCreateSettings',
  async (levelId, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getOrCreateSettings({ level_id: levelId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateNotificationSettings = createAsyncThunk(
  'notification/updateSettings',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.updateNotificationSettings(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchNotificationHistory = createAsyncThunk(
  'notification/fetchHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getNotificationHistory(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAPIKey = createAsyncThunk(
  'notification/updateAPIKey',
  async (apiKey, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.updateAPIKey(apiKey);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const testAPIKey = createAsyncThunk(
  'notification/testAPIKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.testAPIKey();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Queue-related thunks
export const fetchQueuedNotifications = createAsyncThunk(
  'notification/fetchQueue',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getQueuedNotifications(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelQueuedNotification = createAsyncThunk(
  'notification/cancelQueued',
  async (id, { rejectWithValue }) => {
    try {
      await notificationAPI.cancelQueuedNotification(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const retryFailedNotification = createAsyncThunk(
  'notification/retryFailed',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.retryFailedNotification(id);
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const recipientDefaults = {
  assignment_created_send_to_student: true,
  assignment_created_send_to_parent: true,
  completion_notification_send_to_student: false,
  completion_notification_send_to_parent: true,
  dday_3_notification_send_to_student: true,
  dday_3_notification_send_to_parent: false,
  dday_1_notification_send_to_student: true,
  dday_1_notification_send_to_parent: false,
  overdue_notification_send_to_student: true,
  overdue_notification_send_to_parent: false,
};

const applyRecipientDefaults = (settings = {}) => ({
  ...recipientDefaults,
  ...settings,
});

const initialState = {
  settings: [],
  currentSettings: null,
  levels: [],
  history: [],
  queue: [],
  apiSettings: null,
  selectedLevel: null,
  loading: {
    settings: false,
    levels: false,
    history: false,
    queue: false,
    apiKey: false,
  },
  error: {
    settings: null,
    levels: null,
    history: null,
    queue: null,
    apiKey: null,
  }
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setSelectedLevel: (state, action) => {
      state.selectedLevel = action.payload;
    },
    clearErrors: (state) => {
      state.error = {
        settings: null,
        levels: null,
        history: null,
        apiKey: null,
      };
    },
    updateCurrentTemplate: (state, action) => {
      const { field, value } = action.payload;
      if (state.currentSettings) {
        state.currentSettings[field] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.loading.settings = true;
        state.error.settings = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.loading.settings = false;
        const payload = Array.isArray(action.payload) ? action.payload : [];
        state.settings = payload.map(applyRecipientDefaults);
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error.settings = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Fetch Levels
      .addCase(fetchLevels.pending, (state) => {
        state.loading.levels = true;
        state.error.levels = null;
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.loading.levels = false;
        state.levels = action.payload;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.loading.levels = false;
        state.error.levels = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Fetch or Create Settings
      .addCase(fetchOrCreateSettings.pending, (state) => {
        state.loading.settings = true;
        state.error.settings = null;
      })
      .addCase(fetchOrCreateSettings.fulfilled, (state, action) => {
        state.loading.settings = false;
        state.currentSettings = applyRecipientDefaults(action.payload.settings);
      })
      .addCase(fetchOrCreateSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error.settings = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Update Settings
      .addCase(updateNotificationSettings.pending, (state) => {
        state.loading.settings = true;
        state.error.settings = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.loading.settings = false;
        const normalized = applyRecipientDefaults(action.payload);
        state.currentSettings = normalized;
        const index = state.settings.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.settings[index] = normalized;
        }
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error.settings = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Fetch History
      .addCase(fetchNotificationHistory.pending, (state) => {
        state.loading.history = true;
        state.error.history = null;
      })
      .addCase(fetchNotificationHistory.fulfilled, (state, action) => {
        state.loading.history = false;
        state.history = action.payload;
      })
      .addCase(fetchNotificationHistory.rejected, (state, action) => {
        state.loading.history = false;
        state.error.history = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Update API Key
      .addCase(updateAPIKey.pending, (state) => {
        state.loading.apiKey = true;
        state.error.apiKey = null;
      })
      .addCase(updateAPIKey.fulfilled, (state, action) => {
        state.loading.apiKey = false;
        state.apiSettings = action.payload;
      })
      .addCase(updateAPIKey.rejected, (state, action) => {
        state.loading.apiKey = false;
        state.error.apiKey = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })
      
      // Test API Key
      .addCase(testAPIKey.pending, (state) => {
        state.loading.apiKey = true;
        state.error.apiKey = null;
      })
      .addCase(testAPIKey.fulfilled, (state, action) => {
        state.loading.apiKey = false;
      })
      .addCase(testAPIKey.rejected, (state, action) => {
        state.loading.apiKey = false;
        state.error.apiKey = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })

      // Fetch Queued Notifications
      .addCase(fetchQueuedNotifications.pending, (state) => {
        state.loading.queue = true;
        state.error.queue = null;
      })
      .addCase(fetchQueuedNotifications.fulfilled, (state, action) => {
        state.loading.queue = false;
        state.queue = action.payload;
      })
      .addCase(fetchQueuedNotifications.rejected, (state, action) => {
        state.loading.queue = false;
        state.error.queue = action.payload?.detail || action.payload || action.error?.message || 'Unknown error';
      })

      // Cancel Queued Notification
      .addCase(cancelQueuedNotification.fulfilled, (state, action) => {
        state.queue = state.queue.filter(item => item.id !== action.payload);
      })

      // Retry Failed Notification
      .addCase(retryFailedNotification.fulfilled, (state, action) => {
        const index = state.queue.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.queue[index].status = 'sent';
          state.queue[index].sent_at = new Date().toISOString();
        }
      });
  },
});

export const { setSelectedLevel, clearErrors, updateCurrentTemplate } = notificationSlice.actions;
export default notificationSlice.reducer;
